// MomentumChart.js

import React, { Component } from 'react';
import { connect } from 'react-redux';
import ReactEcharts from 'echarts-for-react';
import Select from 'react-select';
import * as echarts from 'echarts'; // Importing echarts to access graphic utilities
import './MomentumChart.css';
import { timelineColors } from './palette'; // Ensure this palette has sufficient colors
import i18n from '../../../i18n';

// Custom styles for react-select
const customStyles = {
  control: (base) => ({
    ...base,
    borderRadius: 0,
    boxShadow: 'none',
  }),
  option: (provided) => ({
    ...provided,
    color: '#5b6770',
    fontSize: 14,
    borderRadius: 0,
    backgroundColor: 'white',
    ':hover': {
      backgroundColor: '#050139',
      color: 'white',
    },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 0,
  }),
  singleValue: (provided, state) => {
    const opacity = state.isDisabled ? 0.5 : 1;
    const transition = 'opacity 100ms';
    return { ...provided, opacity, transition };
  },
  dropdownIndicator: (base) => ({
    ...base,
    color: '#050139',
  }),
};

function formatDate(date) {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj)) return '';
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const year = dateObj.getFullYear();
  return `${month}/${day}/${year}`;
}

function dataFormatter(value, type) {
  if (typeof value !== 'number' || isNaN(value)) {
    return 'N/A';
  }

  if (type === 'axis') {
    return `$${value.toFixed(0)}`;
  } else if (type === 'data') {
    return `$${value.toFixed(2)}`;
  } else {
    return `$${value.toFixed(2)}`;
  }
}

function calculateRSI(values, period = 14) {
  if (!values || values.length < period + 1) {
    return new Array(values.length).fill(null);
  }

  const rsi = new Array(values.length).fill(null);

  // Step 1: Calculate initial average gain/loss over the first `period` bars
  let gains = 0;
  let losses = 0;
  for (let i = 1; i <= period; i++) {
    const change = values[i] - values[i - 1];
    if (change > 0) {
      gains += change;
    } else {
      losses += -change;
    }
  }

  let avgGain = gains / period;
  let avgLoss = losses / period;

  // First RSI value occurs at index = period (because we need period+1 values to seed)
  rsi[period] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);

  // Step 2: Wilder’s smoothing for the rest
  for (let i = period + 1; i < values.length; i++) {
    const change = values[i] - values[i - 1];
    const gain = change > 0 ? change : 0;
    const loss = change < 0 ? -change : 0;

    // Wilder’s smoothed averages
    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;

    rsi[i] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  }

  return rsi;
}

function chartData(fund) {
  const { name, ticker, rollingHorizons } = fund;
  const momentum = rollingHorizons?.rollingMomentum;
  if (!momentum) return null;

  const {
    dates, // array of raw date strings (e.g. "2023-06-01T00:00:00Z")
    dlyNavs,
    dly50dNavAvgs,
    dly100dNavAvgs,
    dly200dNavAvgs,
  } = momentum;
  if (!dates || !dlyNavs || !dly50dNavAvgs) return null;

  // 1) Convert each date string into a JS Date object
  const dateObjs = dates.map((d) => new Date(d));

  // 2) Identify the cutoff that is 200 days before the last date in the array
  const lastDate = dateObjs[dateObjs.length - 1];
  const cutoff200 = new Date(lastDate);
  cutoff200.setDate(cutoff200.getDate() - 200);

  // 3) Find the first index where dateObjs[i] >= cutoff200
  const startIndex = dateObjs.findIndex((dt) => dt >= cutoff200);
  const sliceIndex = startIndex === -1 ? 0 : startIndex;

  // 4) Prepare full arrays for plotting
  const chartDates = dates.map(formatDate);
  const nav = dlyNavs.map(Number);
  const avg50 = dly50dNavAvgs.map((v) => (v != null ? +v : null));
  const avg100 = dly100dNavAvgs.map((v) => (v != null ? +v : null));
  const avg200 = dly200dNavAvgs.map((v) => (v != null ? +v : null));
  const rsi = calculateRSI(nav, 14);

  // 5) Slice the “recent” (last 200 days) portion of each numeric array
  const navRecent = nav.slice(sliceIndex);
  const avg50Recent = avg50.slice(sliceIndex);
  const avg100Recent = avg100.slice(sliceIndex);
  const avg200Recent = avg200.slice(sliceIndex);

  // 6) Combine all recent values and filter out null/NaN to compute bounds
  const recentVals = [
    ...navRecent,
    ...avg50Recent,
    ...avg100Recent,
    ...avg200Recent,
  ].filter((v) => v != null && !isNaN(v));
  // If there’s no data in the last 200 days, fall back to the entire history
  const allValsForBounds =
    recentVals.length > 0
      ? recentVals
      : [...nav, ...avg50, ...avg100, ...avg200].filter(
          (v) => v != null && !isNaN(v)
        );

  // 7) Compute raw min/max (before padding)
  const rawMin = Math.min(...allValsForBounds);
  const rawMax = Math.max(...allValsForBounds);

  // 8) Find dates associated with rawMin and rawMax (for console logging)
  let minDate = null;
  let maxDate = null;

  for (let i = sliceIndex; i < nav.length; i++) {
    if (nav[i] === rawMin) {
      minDate = dates[i];
      break;
    }
  }
  if (!minDate) {
    for (let i = sliceIndex; i < avg50.length; i++) {
      if (avg50[i] === rawMin) {
        minDate = dates[i];
        break;
      }
    }
  }
  if (!minDate) {
    for (let i = sliceIndex; i < avg100.length; i++) {
      if (avg100[i] === rawMin) {
        minDate = dates[i];
        break;
      }
    }
  }
  if (!minDate) {
    for (let i = sliceIndex; i < avg200.length; i++) {
      if (avg200[i] === rawMin) {
        minDate = dates[i];
        break;
      }
    }
  }

  for (let i = sliceIndex; i < nav.length; i++) {
    if (nav[i] === rawMax) {
      maxDate = dates[i];
      break;
    }
  }
  if (!maxDate) {
    for (let i = sliceIndex; i < avg50.length; i++) {
      if (avg50[i] === rawMax) {
        maxDate = dates[i];
        break;
      }
    }
  }
  if (!maxDate) {
    for (let i = sliceIndex; i < avg100.length; i++) {
      if (avg100[i] === rawMax) {
        maxDate = dates[i];
        break;
      }
    }
  }
  if (!maxDate) {
    for (let i = sliceIndex; i < avg200.length; i++) {
      if (avg200[i] === rawMax) {
        maxDate = dates[i];
        break;
      }
    }
  }

  // 9) Log raw min/max and their dates
  console.log(
    `🔍 [${ticker}] rawMin: ${rawMin} at ${formatDate(
      minDate
    )}, rawMax: ${rawMax} at ${formatDate(maxDate)}`
  );

  // 10) Add 5% padding and round to the nearest multiple of 5
  let yMin = rawMin;
  let yMax = rawMax;
  const pad = (yMax - yMin) * 0.05;
  yMin = Math.floor((yMin - pad) / 5) * 5;
  yMax = Math.ceil((yMax + pad) / 5) * 5;

  // 11) Log padded yMin/yMax as well

  // 12) Build the series for the chart (full arrays used for plotting)
  const series = [
    {
      name: `${ticker} (${name}) - NAV`,
      data: nav,
      type: 'line',
      smooth: true,
      showSymbol: false,
      areaStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: timelineColors[0] },
          { offset: 1, color: 'rgba(0,0,0,0)' },
        ]),
      },
      lineStyle: { color: timelineColors[0], width: 2 },
    },
    {
      name: '50D Avg',
      data: avg50,
      type: 'line',
      smooth: true,
      showSymbol: false,
      lineStyle: {
        color: timelineColors[1],
        width: 2,
        type: 'dashed',
      },
    },
    {
      name: '100D Avg',
      data: avg100,
      type: 'line',
      smooth: true,
      showSymbol: false,
      lineStyle: {
        color: timelineColors[2],
        width: 2,
        type: 'dotted',
      },
    },
    {
      name: '200D Avg',
      data: avg200,
      type: 'line',
      smooth: true,
      showSymbol: false,
      lineStyle: {
        color: timelineColors[3],
        width: 2,
      },
    },
    // RSI series on the lower grid/axes
    {
      name: 'RSI (14D)',
      data: rsi,
      type: 'line',
      xAxisIndex: 1,
      yAxisIndex: 1,
      smooth: true,
      showSymbol: false,
      lineStyle: {
        color: '#5b6770',
        width: 2,
        type: 'solid',
      },
      markLine: {
        silent: true,
        symbol: 'none',
        data: [
          { yAxis: 30, lineStyle: { color: 'green', width: 1, type: 'solid' } },
          { yAxis: 50, lineStyle: { color: '#666', width: 1, type: 'dashed' } },
          { yAxis: 70, lineStyle: { color: 'red', width: 1, type: 'solid' } },
        ],
      },
    },
  ];

  // 13) Assemble the full chartOptions, plugging in the dynamic yMin/yMax
  const chartOptions = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        link: [{ xAxisIndex: [0, 1] }],
        label: { backgroundColor: '#6a7985' },
        triggerTooltip: true,
        snap: false,
        z: 50,
      },
      backgroundColor: '#FFF',
      borderWidth: 1,
      borderColor: '#050139',
      textStyle: { color: '#5B6770' },
      formatter(params) {
        const rsiItem = params.find((p) => p.axisIndex === 1);
        const date = rsiItem?.name || params[0]?.name;
        let html = `<strong>${date}</strong>`;

        // Sort so RSI always appears at the bottom of the tooltip
        const sortedParams = params.slice().sort((a, b) => {
          if (a.seriesName === 'RSI (14D)') return 1;
          if (b.seriesName === 'RSI (14D)') return -1;
          return 0;
        });

        for (const item of sortedParams) {
          if (item.value != null) {
            const formattedValue =
              item.seriesName === 'RSI (14D)'
                ? (+item.value).toFixed(0)
                : dataFormatter(item.value, 'data');
            html += `<br/>${item.marker} <strong>${item.seriesName}</strong>: ${formattedValue}`;
          }
        }

        return html;
      },
    },
    axisPointer: {
      link: [{ xAxisIndex: [0, 1] }],
      label: { show: true, backgroundColor: '#6a7985' },
    },
    legend: {
      icon: 'circle',
      data: [
        ...new Set(
          series
            .filter((s) => s.yAxisIndex !== 1 || s.xAxisIndex !== 1)
            .map((s) => s.name)
        ),
      ],
      textStyle: { color: '#050139' },
    },
    grid: [
      {
        top: '10%',
        left: '10%',
        right: '10%',
        height: '46%',
      },
      {
        top: '60%',
        left: '10%',
        right: '10%',
        height: '18%',
      },
    ],
    xAxis: [
      {
        type: 'category',
        data: chartDates,
        boundaryGap: false,
        axisLabel: { show: false },
        axisTick: { show: false },
        axisLine: { show: false },
        axisPointer: { label: { show: false } },
      },
      {
        type: 'category',
        gridIndex: 1,
        data: chartDates,
        boundaryGap: false,
        axisLabel: { fontSize: 12, color: '#050139' },
      },
    ],
    yAxis: [
      {
        type: 'value',
        min: yMin, // e.g. 35
        max: yMax, // e.g. 45
        interval: 5, // major tick every 5 units
        splitNumber: 5, // will subdivide each 5‐unit segment into 5 parts (i.e. 1 unit each)
        minorTick: {
          show: true, // enable minor ticks
          splitNumber: 5, // 5 subdivisions between each major tick
        },
        minorSplitLine: {
          show: true, // draw minor grid lines at those 1‐unit intervals
          lineStyle: {
            color: '#eee', // lighter color for minor grid
            width: 1,
          },
        },
        gridIndex: 0,
        position: 'left',
        axisLabel: {
          fontSize: 12,
          color: '#050139',
          formatter: (val) => dataFormatter(val, 'axis'),
        },
      },
      {
        type: 'value',
        min: 0,
        max: 100,
        gridIndex: 1,
        position: 'left',
        interval: 10,
        axisLabel: {
          fontSize: 12,
          color: '#050139',
          formatter: (val) => ([30, 50, 70].includes(val) ? val : ''),
        },
        splitLine: { show: false },
      },
    ],

    dataZoom: [
      {
        type: 'inside',
        xAxisIndex: [0, 1],
        start: 0,
        end: 100,
      },
      {
        type: 'slider',
        xAxisIndex: [0, 1],
        start: 0,
        end: 100,
        top: '85%',
        height: 20,
        handleSize: '100%',
        handleStyle: { color: '#050139' },
        showDetail: false,
      },
    ],
    graphic: [
      {
        type: 'text',
        left: 'center',
        top: '58%',
        style: {
          text: 'Relative Strength Index (RSI)',
          font: '14px sans-serif',
          fill: '#050139',
        },
      },
    ],
    series,
    color: timelineColors,
  };

  // 14) Compute the “last” index for average‐price labels (unchanged)
  const last = nav.length - 1;
  const avgPrices = {
    NAV: nav[last]?.toFixed(2) ?? 'N/A',
    '50D': avg50[last]?.toFixed(2) ?? 'N/A',
    '100D': avg100[last]?.toFixed(2) ?? 'N/A',
    '200D': avg200[last]?.toFixed(2) ?? 'N/A',
  };

  const chartTitle = `Moving Average & RSI for ${name} (${ticker})`;

  return { chartOptions, avgPrices, chartTitle };
}

class MomentumChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedFundIndex: null, // Will set to the first valid fund’s data index
      chartOptions: {},
      avgPrices: {
        NAV: 'N/A',
        '50D': 'N/A',
        '100D': 'N/A',
        '200D': 'N/A',
      },
      chartTitle: '',
      error: '',
    };

    this.echartsRef = React.createRef(); // Reference to ECharts instance
  }

  componentDidMount() {
    this.setInitialFund();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.data !== this.props.data) {
      this.setInitialFund();
    }

    // Re-initialize chart if selectedFundIndex changes
    if (prevState.selectedFundIndex !== this.state.selectedFundIndex) {
      this.initializeChart();
    }
  }

  /**
   * Sets the initial selected fund to the first valid fund in the data.
   */
  setInitialFund = () => {
    const { data } = this.props;
    if (data && data.length > 0) {
      // Find the first valid fund
      const validFund = data.find(
        (fund) =>
          fund.rollingHorizons &&
          fund.rollingHorizons.rollingMomentum &&
          fund.rollingHorizons.rollingMomentum.dates.length > 0 &&
          fund.rollingHorizons.rollingMomentum.dlyNavs.length > 0
      );

      if (validFund) {
        const index = data.indexOf(validFund);
        if (index !== this.state.selectedFundIndex) {
          this.setState({ selectedFundIndex: index });
        }
      } else {
        // No valid fund
        this.setState({
          chartOptions: null,
          avgPrices: {
            NAV: 'N/A',
            '50D': 'N/A',
            '100D': 'N/A',
            '200D': 'N/A',
          },
          chartTitle: '',
          error: 'No data available.',
        });
      }
    } else {
      // No data
      this.setState({
        chartOptions: null,
        avgPrices: {
          NAV: 'N/A',
          '50D': 'N/A',
          '100D': 'N/A',
          '200D': 'N/A',
        },
        chartTitle: '',
        error: 'No data available.',
      });
    }
  };

  /**
   * Initializes or updates the chart options based on current state and props.
   */
  initializeChart = () => {
    const { data } = this.props;
    const { selectedFundIndex } = this.state;

    if (selectedFundIndex === null || selectedFundIndex >= data.length) {
      this.setState({
        chartOptions: null,
        avgPrices: {
          NAV: 'N/A',
          '50D': 'N/A',
          '100D': 'N/A',
          '200D': 'N/A',
        },
        chartTitle: '',
        error: 'No data available.',
      });
      return;
    }

    const selectedFund = data[selectedFundIndex];
    const chartDataResult = chartData(selectedFund);

    if (!chartDataResult) {
      this.setState({
        chartOptions: null,
        avgPrices: {
          NAV: 'N/A',
          '50D': 'N/A',
          '100D': 'N/A',
          '200D': 'N/A',
        },
        chartTitle: '',
        error: 'No valid data available for the selected fund.',
      });
      return;
    }

    const { chartOptions, avgPrices, chartTitle } = chartDataResult;

    this.setState({
      chartOptions,
      avgPrices,
      chartTitle,
      error: '',
    });
  };

  /**
   * Handler for fund selection change.
   * @param {Object|null} selectedOption - The selected fund option or null.
   */
  handleFundChange = (selectedOption) => {
    if (selectedOption) {
      this.setState({ selectedFundIndex: selectedOption.value });
    } else {
      // If no option is selected, reset to the first valid fund
      this.setInitialFund();
    }
  };

  render() {
    const { data } = this.props;
    const { chartOptions, error, avgPrices, selectedFundIndex, chartTitle } =
      this.state;

    // Prepare react-select options, filtering out funds without valid rollingMomentum data
    const dropdownOptions =
      data && data.length > 0
        ? data
            .map((fund, index) => {
              const hasValidData =
                fund.rollingHorizons &&
                fund.rollingHorizons.rollingMomentum &&
                Array.isArray(fund.rollingHorizons.rollingMomentum.dates) &&
                Array.isArray(fund.rollingHorizons.rollingMomentum.dlyNavs) &&
                Array.isArray(
                  fund.rollingHorizons.rollingMomentum.dly50dNavAvgs
                ) &&
                Array.isArray(
                  fund.rollingHorizons.rollingMomentum.dly100dNavAvgs
                ) &&
                Array.isArray(
                  fund.rollingHorizons.rollingMomentum.dly200dNavAvgs
                ) &&
                fund.rollingHorizons.rollingMomentum.dates.length > 0 &&
                fund.rollingHorizons.rollingMomentum.dlyNavs.length > 0;

              if (hasValidData) {
                return {
                  value: index, // data index
                  label: `${fund.ticker} (${fund.name})`, // Ticker first, followed by name
                };
              } else {
                return null; // Exclude funds without valid data
              }
            })
            .filter((option) => option !== null) // Remove null entries
        : [];

    // Find the selected option based on selectedFundIndex
    const selectedOption =
      selectedFundIndex !== null &&
      dropdownOptions.some((opt) => opt.value === selectedFundIndex)
        ? dropdownOptions.find((opt) => opt.value === selectedFundIndex)
        : null;

    // If selectedFundIndex is not set yet, show a loading message
    if (selectedFundIndex === null && dropdownOptions.length > 0) {
      // This case occurs before setInitialFund completes
      return <div className="loading-message">Loading...</div>;
    }

    return (
      <div className="momentum-chart">
        {/* Chart Header */}
        <div className="chartHeader">{i18n.t('TECHNICALS')}</div>

        {/* Dynamic Chart Title */}
        {chartTitle && <div className="dynamic-chart-title">{chartTitle}</div>}

        {/* Controls Container: Select and Average Labels on the Same Row */}
        <div className="controls-container">
          {/* Fund Selection Dropdown */}
          <div className="fund-select-container">
            <Select
              id="fund-select"
              styles={customStyles}
              value={selectedOption}
              onChange={this.handleFundChange}
              options={dropdownOptions}
              placeholder="Select a fund..."
              isSearchable
            />
          </div>

          {/* Labels in the Middle */}
          <div className="average-labels-container">
            <div className="average-label">
              <strong>Last NAV:</strong>{' '}
              {avgPrices.NAV !== 'N/A' ? avgPrices.NAV : 'N/A'}
            </div>
            <div className="average-label">
              <strong>50D Avg:</strong>{' '}
              {avgPrices['50D'] !== 'N/A' ? avgPrices['50D'] : 'N/A'}
            </div>
            <div className="average-label">
              <strong>100D Avg:</strong>{' '}
              {avgPrices['100D'] !== 'N/A' ? avgPrices['100D'] : 'N/A'}
            </div>
            <div className="average-label">
              <strong>200D Avg:</strong>{' '}
              {avgPrices['200D'] !== 'N/A' ? avgPrices['200D'] : 'N/A'}
            </div>
          </div>
        </div>

        {/* Display Error Message if Any */}
        {error && <div className="error-message">{error}</div>}

        {/* Chart Container */}
        <div className="chartContainer">
          {chartOptions &&
          chartOptions.series &&
          chartOptions.series.length > 0 ? (
            <ReactEcharts
              key={`momentum-chart-${selectedFundIndex}`} // Unique key to force re-render on state change
              ref={(e) => (this.echartsRef = e)}
              option={chartOptions}
              notMerge={true}
              theme="default"
              style={{ height: 500, width: '100%' }} // Responsive width
              onChartReady={() => {}}
            />
          ) : (
            // Only show "No data available" if selectedFundIndex is set and no chartOptions
            selectedFundIndex !== null && (
              <div className="no-data-message">
                No data available for the selected fund.
              </div>
            )
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    data: state.fundData.data, // Ensure that data is correctly populated in the Redux store
  };
};

export default connect(mapStateToProps, {})(MomentumChart);
